"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Sparkles, Coins } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function AceOfPentaclesPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-green-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Minör Arkana</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Pentagramlar</span>
            <span className="text-green-500">/</span>
            <span className="text-sm">Pentagramlar Ası</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Minör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/pentacles"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Pentagramlar
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">Pentagramlar Ası</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 rounded-xl p-8 mb-8 border border-green-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-ace-of-pentacles.svg"
                  alt="Ace of Pentacles Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-full flex items-center justify-center">
                  <Coins className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">Ace of Pentacles</h1>
                  <p className="text-xl text-green-400">Pentagramlar Ası / Yeni Fırsatlar</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Maddi yeni başlangıçlar, fırsatlar ve bolluk tohumlarının kartı.
                İş, para ve pratik başarılar konularında müjdeli gelişmeleri temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Yeni fırsatlar</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Maddi başlangıç</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Bolluk</span>
                <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Güvenlik</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeni İş Fırsatları</h3>
                  <p className="text-slate-400 text-sm">İş teklifi, proje başlangıcı ve kariyer atılımı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Maddi Kazanç</h3>
                  <p className="text-slate-400 text-sm">Para girişi, yatırım fırsatı ve gelir artışı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sağlık İyileşmesi</h3>
                  <p className="text-slate-400 text-sm">Fiziksel iyileşme, enerji artışı ve vitalite.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Somut Hediye</h3>
                  <p className="text-slate-400 text-sm">Değerli eşyalar, mülk ve maddi hediyeler.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Kaçırılan Fırsatlar</h3>
                  <p className="text-slate-400 text-sm">İş fırsatlarını kaçırma, geç kalma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Maddi Kayıplar</h3>
                  <p className="text-slate-400 text-sm">Para kaybı, yanlış yatırımlar ve israf.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sağlık Sorunları</h3>
                  <p className="text-slate-400 text-sm">Fiziksel problemler ve enerji düşüklüğü.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Aşırı Materializm</h3>
                  <p className="text-slate-400 text-sm">Sadece maddi değerlere odaklanma.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Coins className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Pentagram</h3>
              <p className="text-slate-400 text-sm">Maddi bolluk, güvenlik ve dünyevi güç.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">✋</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">El</h3>
              <p className="text-slate-400 text-sm">Divine hediye ve fırsat sunumu.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌸</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Bahçe</h3>
              <p className="text-slate-400 text-sm">Bereketli toprak ve büyüme potansiyeli.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🛤️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yol</h3>
              <p className="text-slate-400 text-sm">Pratik adımlar ve ileriye doğru hareket.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🌿</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yeşillik</h3>
              <p className="text-slate-400 text-sm">Doğal büyüme ve bereket.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🏔️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Dağlar</h3>
              <p className="text-slate-400 text-sm">Sağlam temel ve kararlılık.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Güvenli ilişki temeli</li>
                <li>• Maddi güvenlik birliktelikte</li>
                <li>• Pratik aşk gösterileri</li>
                <li>• Ortak gelecek planları</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Yeni iş teklifi</li>
                <li>• Yatırım fırsatı</li>
                <li>• Proje başlangıcı</li>
                <li>• Maddi kazanç</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Pratik beceriler kazanma</li>
                <li>• Maddi bağımsızlık</li>
                <li>• Özgüven artışı</li>
                <li>• Somut hedefler</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Coins className="h-5 w-5 text-blue-400" />
                Sağlık ve Yaşam
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Sağlık iyileşmesi</li>
                <li>• Fiziksel güç artışı</li>
                <li>• İyi beslenme</li>
                <li>• Yaşam kalitesi yükselme</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/pentacles"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Pentagramlar
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/minor-arcana/pentacles/two-of-pentacles"
            className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            İki Pentagram
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}